import React from 'react';
import { Breadcrumb, Tree, Skeleton, Button, Modal, Typography } from 'antd';
import type { Key } from 'antd/lib/table/interface';
import { SolutionOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Link, useParams, useHistory } from 'react-router-dom';

import ClientFeatureConfigController from '../../controllers/clientFeatureConfig.controller';
import ClientCopyConfigList from './ClientCopyConfigList';
import { useClientCopyConfigContext } from './ClientCopyConfigContext';

const { Title } = Typography;

const ClientCopyConfigTree: React.FC = (): React.ReactElement => {
    const {
        isLoadingLoggedInClient,
        isConfigCopyable,
        isLoadingClientConfigs,
        isNoClientFound,
        loggedInClient,
        selectedClients,
        clientConfigs,
        checkedKeys,
        setSelectedClients,
        setCheckedKeys,
    } = useClientCopyConfigContext();

    const { clientId }: { clientId: string } = useParams();
    const history = useHistory();

    const copyClientFeatureConfigToClients = async (): Promise<void> => {
        await ClientFeatureConfigController.copyClientFeatureConfigToClients(
            Number(clientId),
            checkedKeys,
            selectedClients,
        );
    };

    const onCheck = (
        checked:
            | {
                  checked: Key[];
                  halfChecked: Key[];
              }
            | Key[],
        _info: any,
    ) => {
        if (!Array.isArray(checked)) return;
        setCheckedKeys(checked);
    };

    const confirm = () => {
        Modal.confirm({
            title: 'Confirm',
            icon: <ExclamationCircleOutlined />,
            content: 'This will erase and replace the config on the target client.',
            okText: 'Confirm',
            cancelText: 'Cancel',
            onOk: () => {
                copyClientFeatureConfigToClients();
                history.push('/admin/clients');
            },
        });
    };

    return (
        <>
            <Breadcrumb className="mb-3">
                <Breadcrumb.Item>
                    <SolutionOutlined className="d-inline-flex mr-1" />
                    <Link to="/admin/clients" title="Clients">
                        Clients
                    </Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>Config Copy</Breadcrumb.Item>
                <Breadcrumb.Item>{loggedInClient?.name}</Breadcrumb.Item>
            </Breadcrumb>
            {isNoClientFound || isLoadingClientConfigs || isLoadingLoggedInClient ? (
                <Skeleton active className="p-4" />
            ) : (
                <>
                    <Title level={5}>Choose the config features you&apos;d like to copy</Title>
                    <Tree checkable treeData={clientConfigs} onCheck={onCheck} />
                </>
            )}
            <br />
            <>
                <ClientCopyConfigList setSelectedClients={setSelectedClients} selectedClients={selectedClients} />
            </>
            <Button type={isConfigCopyable ? 'primary' : 'ghost'} disabled={!isConfigCopyable} onClick={confirm}>
                Copy Config
            </Button>
        </>
    );
};

export default ClientCopyConfigTree;
